import type {
  AnnotationOperationAvailability,
  AnnotationOperationError,
  AnnotationStudioLabels,
  AnnotationStudioLocale,
  AnnotationStudioLocalizedText,
  AnnotationStudioMessage,
} from "./types";

export const defaultAnnotationStudioLabels: AnnotationStudioLabels = {
  collection: { en: "Dataset", ko: "데이터셋" },
  sequence: { en: "Episode", ko: "에피소드" },
  stream: { en: "Camera", ko: "카메라" },
  target: { en: "Target", ko: "타겟" },
};

export const annotationStudioMessages = {
  detachFramePreview: { en: "Open preview in popup", ko: "팝업으로 분리" },
  dockFramePreview: { en: "Move preview into panel", ko: "패널에 넣기" },
  dockFramePreviewCompact: {
    en: "Compact slots and move preview into panel",
    ko: "슬롯을 축약해 패널에 넣기",
  },
  dockFramePreviewUnavailable: {
    en: "Not enough room in the panel",
    ko: "패널에 미리보기를 넣을 공간이 부족합니다",
  },
  expandAnnotationSlots: { en: "Expand slots", ko: "슬롯 펼치기" },
  compactAnnotationSlots: { en: "Compact slots", ko: "슬롯 축약" },
  entryLoading: { en: "Loading Annotation Studio…", ko: "Annotation Studio를 불러오는 중…" },
  entryError: {
    en: "Annotation Studio could not be loaded",
    ko: "Annotation Studio를 불러오지 못했습니다",
  },
  entryRetrying: { en: "Retrying Annotation Studio…", ko: "Annotation Studio를 다시 불러오는 중…" },
  entryDescription: {
    en: "The data needed to open the Studio is unavailable. Try loading it again.",
    ko: "Studio를 여는 데 필요한 데이터를 가져오지 못했습니다. 다시 불러와 주세요.",
  },
  entryRetry: { en: "Try again", ko: "다시 시도" },
  title: { en: "Media annotation", ko: "Annotation" },
  captureFrame: { en: "Capture frame", ko: "프레임 캡처" },
  frameAlreadyCaptured: { en: "Frame already captured", ko: "이미 캡처된 프레임" },
  removeCapturedFrame: { en: "Remove captured frame", ko: "캡처 프레임 제거" },
  removingCapturedFrame: { en: "Removing captured frame", ko: "캡처 프레임 제거 중" },
  removeCapturedFrameTitle: {
    en: "Remove this captured frame?",
    ko: "이 캡처 프레임을 제거할까요?",
  },
  removeCapturedFrameDescription: {
    en: "This removes the frame and every annotation in it. This action cannot be undone.",
    ko: "프레임과 프레임 안의 모든 Annotation이 함께 삭제되며 되돌릴 수 없습니다.",
  },
  capturingFrame: { en: "Capturing frame", ko: "프레임 캡처 중" },
  frameCaptureMode: { en: "Frame capture", ko: "프레임 캡처" },
  annotationEditMode: { en: "Annotation edit", ko: "Annotation 편집" },
  editPlayheadAnnotation: {
    en: "Edit annotation for the captured frame at the playhead",
    ko: "현재 캡처 프레임의 Annotation 편집",
  },
  viewPlayheadAnnotation: {
    en: "View annotation for the captured frame at the playhead",
    ko: "현재 캡처 프레임의 Annotation 보기",
  },
  segment: { en: "Generate mask", ko: "마스크 생성" },
  segmenting: { en: "Generating mask", ko: "마스크 생성 중" },
  save: { en: "Save current frame", ko: "현재 프레임 저장" },
  saving: { en: "Saving current frame", ko: "현재 프레임 저장 중" },
  savingStatus: { en: "Saving…", ko: "저장 중…" },
  saveCameraAnnotations: {
    en: "Save all annotation changes for this camera",
    ko: "이 카메라의 Annotation 변경 사항 모두 저장",
  },
  saveCameraAnnotationsAction: {
    en: "Save all",
    ko: "모두 저장",
  },
  savingCameraAnnotations: {
    en: "Saving all annotation changes for this camera",
    ko: "이 카메라의 Annotation 변경 사항 저장 중",
  },
  savingCameraAnnotationsAction: {
    en: "Saving all",
    ko: "모두 저장 중",
  },
  noCameraAnnotationChanges: {
    en: "No annotation changes to save for this camera",
    ko: "이 카메라에 저장할 Annotation 변경 사항이 없습니다",
  },
  exitStudio: { en: "Exit studio", ko: "Studio 나가기" },
  saveAndExit: { en: "Save and exit", ko: "저장하고 나가기" },
  saveAndExitAnyway: { en: "Save and exit anyway", ko: "그래도 저장하고 나가기" },
  continueAnyway: { en: "Continue anyway", ko: "그래도 계속" },
  savingAndExit: { en: "Saving and exiting…", ko: "저장 후 나가는 중…" },
  exitingStudio: { en: "Exiting studio…", ko: "Studio 나가는 중…" },
  exitStudioFailed: {
    en: "The Studio could not be closed. Try again.",
    ko: "Studio를 나가지 못했습니다. 다시 시도해 주세요.",
  },
  annotationSetCheck: { en: "Annotation set check", ko: "전체 Annotation 점검" },
  checkAllAnnotations: { en: "Check all frames", ko: "전체 프레임 점검" },
  checkAgain: { en: "Check again", ko: "다시 점검" },
  saveToCheckAnnotations: { en: "Save to check", ko: "저장 후 점검" },
  checkingAnnotations: { en: "Checking all frames…", ko: "전체 프레임 점검 중…" },
  annotationSetReady: {
    en: "All frames passed the completion checks.",
    ko: "모든 프레임이 완료 조건을 충족했습니다.",
  },
  annotationSetBlocked: {
    en: "Some frames need attention before completion.",
    ko: "완료하기 전에 확인해야 할 프레임이 있습니다.",
  },
  annotationSetStale: {
    en: "Save the changed frame to check all annotations again automatically.",
    ko: "변경한 프레임을 저장하면 전체 Annotation을 자동으로 다시 점검합니다.",
  },
  annotationSetCheckFailed: {
    en: "The annotation check could not be completed.",
    ko: "Annotation 점검을 완료하지 못했습니다.",
  },
  annotationSetCheckFailedTitle: { en: "Check failed", ko: "점검 실패" },
  annotationSetNotCheckedTitle: { en: "Not checked", ko: "점검 전" },
  annotationSetNotChecked: {
    en: "Save a frame annotation to run the check automatically.",
    ko: "프레임 Annotation을 저장하면 자동으로 점검합니다.",
  },
  annotationSetNoAnnotationChanges: {
    en: "Add an annotation to a captured frame before checking the set.",
    ko: "캡처 프레임에 Annotation을 추가하면 전체 점검을 진행할 수 있습니다.",
  },
  annotationSetNeedsAnnotation: {
    en: "Add an annotation to at least one captured frame before completing this set.",
    ko: "이 Annotation set을 완료하려면 캡처 프레임 하나 이상에 Annotation을 추가하세요.",
  },
  annotationSetAwaitingCheck: {
    en: "This annotation set has not been checked yet.",
    ko: "이 Annotation set은 아직 점검하지 않았습니다.",
  },
  annotationSetCheckOutdated: {
    en: "The previous check is out of date. Check all frames again.",
    ko: "이전 점검 결과가 오래되었습니다. 전체 프레임을 다시 점검하세요.",
  },
  annotationIssue: { en: "issue", ko: "개 문제" },
  annotationIssues: { en: "issues", ko: "개 문제" },
  relatedFrames: { en: "Related frames", ko: "연관 프레임" },
  needsAttention: { en: "Needs attention", ko: "확인 필요" },
  addMarkerHint: {
    en: "Click the frame to add annotation markers",
    ko: "프레임을 클릭해 Annotation 마커를 추가하세요",
  },
  includePoint: { en: "Add include point", ko: "포함 포인트 추가" },
  excludePoint: { en: "Add exclude point", ko: "제외 포인트 추가" },
  undoPoint: { en: "Undo point", ko: "포인트 실행 취소" },
  undoAnnotation: { en: "Undo annotation edit", ko: "Annotation 편집 실행 취소" },
  redoAnnotation: { en: "Redo annotation edit", ko: "Annotation 편집 다시 실행" },
  clearTargetAnnotation: { en: "Clear annotation", ko: "Annotation 지우기" },
  clearAllTargets: { en: "Clear all", ko: "전체 지우기" },
  // 확인 다이얼로그 전용 라벨 — "전체 지우기"만으로는 프레임 삭제로 오독될 수 있어
  // 삭제 범위(annotation 한정)를 라벨에 명시한다.
  clearAllTargetsConfirm: { en: "Clear all annotations", ko: "Annotation 전체 지우기" },
  clearAllTargetsTitle: {
    en: "Clear all target annotations?",
    ko: "모든 타겟 Annotation을 지울까요?",
  },
  clearAllTargetsDescription: {
    en: "This clears every target's points and mask in the current frame.",
    ko: "현재 프레임에 있는 모든 타겟의 포인트와 마스크를 지웁니다.",
  },
  discardChanges: { en: "Discard changes", ko: "변경 사항 초기화" },
  discardChangesTitle: {
    en: "Discard unsaved annotation changes?",
    ko: "저장하지 않은 Annotation 변경 사항을 초기화할까요?",
  },
  discardSavedChangesDescription: {
    en: "This restores point and mask changes to the last saved state. Captured frames are kept.",
    ko: "포인트와 마스크를 마지막 저장 상태로 되돌립니다. 캡처 프레임은 유지됩니다.",
  },
  discardNewDraftDescription: {
    en: "This clears unsaved point and mask changes. Captured frames are kept.",
    ko: "저장하지 않은 포인트와 마스크를 지웁니다. 캡처 프레임은 유지됩니다.",
  },
  cancel: { en: "Cancel", ko: "취소" },
  renameTarget: { en: "Rename", ko: "이름 바꾸기" },
  capturedFrame: { en: "Captured frame", ko: "캡처된 프레임" },
  capturedFrameCount: { en: "captured frames", ko: "캡처 프레임" },
  frameDisplayScope: { en: "Frame display", ko: "프레임 표시" },
  allEpisodes: { en: "All episodes", ko: "모든 에피소드" },
  newSequence: { en: "New", ko: "신규" },
  selectedEpisodeFrames: { en: "Selected episode", ko: "선택 에피소드" },
  showAllFrames: { en: "Show all episodes", ko: "모든 에피소드 보기" },
  frameInSelectedEpisode: {
    en: "Frame in selected episode",
    ko: "선택한 에피소드의 프레임",
  },
  outsideCurrentFilter: {
    en: "Outside the current filter",
    ko: "현재 필터 범위 밖",
  },
  selectedEpisodePinnedGroup: {
    en: "Pinned episode",
    ko: "고정된 에피소드",
  },
  selectedEpisodeOutsideFilter: {
    en: "Pinned episode. Not included in the current filter results",
    ko: "고정된 에피소드. 현재 필터 결과에는 포함되지 않음",
  },
  framePreview: { en: "Annotation preview", ko: "Annotation 미리보기" },
  currentlyPreviewing: { en: "Currently previewing", ko: "현재 미리보기 중" },
  closeFramePreview: { en: "Close annotation preview", ko: "Annotation 미리보기 닫기" },
  previewCapturedFrame: { en: "Preview annotation", ko: "Annotation 미리보기" },
  selectCapturedFrame: { en: "Select captured frame", ko: "캡처 프레임 선택" },
  addAnnotation: { en: "Add annotation", ko: "Annotation 추가" },
  editAnnotation: { en: "Edit annotation", ko: "Annotation 편집" },
  editingAnnotation: { en: "Editing annotation", ko: "Annotation 편집 중" },
  viewAnnotation: { en: "View annotation", ko: "Annotation 보기" },
  viewingAnnotation: { en: "Viewing annotation", ko: "Annotation 보는 중" },
  editingFrame: { en: "Currently editing", ko: "현재 편집 중" },
  capturedFramePagination: { en: "Captured frame pages", ko: "캡처 프레임 페이지" },
  previousFramePage: { en: "Previous frame page", ko: "이전 프레임 페이지" },
  nextFramePage: { en: "Next frame page", ko: "다음 프레임 페이지" },
  emptyCapturedFrames: {
    en: "Choose a time on the timeline, then capture a frame.",
    ko: "타임라인에서 시점을 선택한 뒤 프레임을 캡처하세요.",
  },
  noCapturedFramesForContext: {
    en: "No captured frames are available for this camera.",
    ko: "선택한 카메라에 캡처된 프레임이 없습니다.",
  },
  noCapturedFramesForEpisode: {
    en: "There are no captured frames in this episode.",
    ko: "이 에피소드에는 캡처된 프레임이 없습니다.",
  },
  selectCapturedFrameToAnnotate: {
    en: "Select a captured frame to annotate.",
    ko: "Annotation을 편집할 캡처 프레임을 선택하세요.",
  },
  selectThisCapturedFrameFirst: {
    en: "Select this captured frame first.",
    ko: "먼저 이 캡처 프레임을 선택하세요.",
  },
  annotationEntryGuide: {
    en: "Select a timeline marker or captured frame in the list to edit its annotation.",
    ko: "타임라인 마커나 목록의 캡처 프레임을 선택해 Annotation을 편집할 수 있습니다.",
  },
  annotationEditAvailable: {
    en: "Edit the annotation for the captured frame at the playhead.",
    ko: "현재 캡처 프레임의 Annotation을 편집할 수 있습니다.",
  },
  goToRawVideo: { en: "Open frame capture", ko: "프레임 캡처 화면으로 이동" },
  goToFrameCapture: { en: "Open frame capture", ko: "프레임 캡처 화면으로 이동" },
  liveVideo: { en: "Live video browser", ko: "비디오 탐색" },
  viewportMode: { en: "Viewport mode", ko: "화면 모드" },
  videoContext: { en: "Video", ko: "영상" },
  annotationContext: { en: "Annotation", ko: "Annotation" },
  capturedFrameEvents: { en: "Captured frames", ko: "캡처 프레임" },
  timelineMarkerConflict: { en: "Conflict", ko: "충돌" },
  nearestCapturedFrame: {
    en: "Go to nearest captured frame",
    ko: "가장 가까운 캡처 프레임으로 이동",
  },
  rawVideoMode: { en: "Raw video", ko: "Raw video" },
  annotationFrameMode: { en: "Annotation frame", ko: "Annotation frame" },
  trackingMode: { en: "Tracking", ko: "Tracking" },
  points: { en: "Points", ko: "포인트" },
  noPoints: { en: "No points yet", ko: "아직 포인트가 없습니다" },
  maskNotReady: {
    en: "Generate a mask to preview it",
    ko: "마스크를 생성하면 미리 볼 수 있습니다",
  },
  maskReady: { en: "Mask ready", ko: "마스크 준비됨" },
  unsaved: { en: "Unsaved changes", ko: "저장하지 않은 변경 사항" },
  saveConflict: { en: "Save conflict", ko: "저장 충돌" },
  saveConflicts: { en: "Save conflicts", ko: "저장 충돌" },
  reviewAnnotationConflicts: { en: "Review issues", ko: "문제 확인" },
  openConflictedFrame: { en: "Open frame", ko: "프레임 열기" },
  framesNeedingAttention: { en: "Frames needing attention", ko: "확인 필요 프레임" },
  unsavedFrames: { en: "Unsaved frames", ko: "미저장 프레임" },
  camerasWithSavedAnnotation: {
    en: "Cameras with saved annotation",
    ko: "저장된 Annotation이 있는 카메라",
  },
  framesWithoutAnnotation: {
    en: "Frames without annotations",
    ko: "Annotation 없는 프레임",
  },
  framesWithAnnotation: {
    en: "Frames with annotations",
    ko: "Annotation 있는 프레임",
  },
  episodeDuration: { en: "Episode duration", ko: "에피소드 길이" },
  unsavedChangesTitle: {
    en: "Leave with unsaved changes?",
    ko: "저장하지 않고 나갈까요?",
  },
  unsavedChangesDescription: {
    en: "Your unsaved annotation changes will be lost if you leave this workspace.",
    ko: "이 작업 공간을 나가면 저장하지 않은 Annotation 변경 사항이 사라집니다.",
  },
  changeCameraTitle: { en: "Change camera?", ko: "카메라를 변경할까요?" },
  changeCameraUnsavedDescription: {
    en: "The current camera has unsaved annotation changes. Save or discard them before changing cameras. Captured frames will be kept.",
    ko: "현재 카메라에 미저장 Annotation이 있습니다. 저장하거나 초기화한 뒤 카메라를 변경하세요. 캡처 프레임은 유지됩니다.",
  },
  saveAndChangeCamera: { en: "Save and change", ko: "저장하고 변경" },
  savingAndChangeCamera: { en: "Saving and changing…", ko: "저장 후 변경 중…" },
  discardAndChangeCamera: {
    en: "Discard and change",
    ko: "초기화하고 변경",
  },
  continueEditing: { en: "Continue editing", ko: "계속 편집" },
  leaveWithoutSaving: { en: "Leave without saving", ko: "저장하지 않고 나가기" },
  saved: { en: "Saved", ko: "저장됨" },
  xCoordinate: { en: "X coordinate", ko: "X 좌표" },
  yCoordinate: { en: "Y coordinate", ko: "Y 좌표" },
  goToCapturedFrame: {
    en: "Go to captured frame",
    ko: "캡처된 프레임으로 이동",
  },
  selectedFrame: { en: "Show captured frame", ko: "캡처된 프레임 보기" },
  browseVideo: { en: "Open frame capture", ko: "프레임 캡처 화면으로 이동" },
  annotationTargets: { en: "Targets", ko: "타겟" },
  objectSlot: { en: "Object slot", ko: "Object slot" },
  usedObjectSlots: { en: "Used object slots", ko: "사용 중인 Object slot" },
  objectSlots: { en: "Object slots", ko: "Object slot" },
  noVisibleObjectSlots: {
    en: "No object slots shown",
    ko: "표시 중인 Object slot 없음",
  },
  showSelectedTargetOnly: { en: "Show selected only", ko: "선택 항목만 보기" },
  showAllTargets: { en: "Show all", ko: "모두 표시" },
  showAllPreviewTargets: { en: "Show all", ko: "전체 보기" },
  hideAllTargets: { en: "Hide all", ko: "모두 숨기기" },
  showTarget: { en: "Show", ko: "표시" },
  hideTarget: { en: "Hide", ko: "숨기기" },
  occupiedAnnotation: { en: "Annotated", ko: "등록됨" },
  emptyAnnotation: { en: "Empty", ko: "비어 있음" },
  positiveMarker: { en: "Positive marker", ko: "포지티브 마커" },
  negativeMarker: { en: "Negative marker", ko: "네거티브 마커" },
  positiveMarkerTooltip: {
    en: "Include point — includes the selected area",
    ko: "포함 포인트 — 선택한 영역을 포함합니다",
  },
  negativeMarkerTooltip: {
    en: "Exclude point — excludes the selected area",
    ko: "제외 포인트 — 선택한 영역을 제외합니다",
  },
  pointInputMode: { en: "Point input mode", ko: "포인트 입력 모드" },
  includePointMode: { en: "Include", ko: "포함" },
  excludePointMode: { en: "Exclude", ko: "제외" },
  coordinateInput: { en: "Coordinate input", ko: "좌표 입력" },
  annotationEditHistory: { en: "Annotation edit history", ko: "Annotation 편집 기록" },
  annotationHelp: { en: "Annotation help", ko: "Annotation 도움말" },
  studioHelp: { en: "Studio help", ko: "Studio 도움말" },
  playbackTimelineShortcuts: { en: "Playback & timeline", ko: "재생 및 타임라인" },
  togglePlaybackShortcut: { en: "Play / pause", ko: "재생 / 일시정지" },
  movePlayheadShortcut: {
    en: "Move the playhead backward / forward",
    ko: "재생 위치를 이전 / 다음으로 이동",
  },
  captureOrEditShortcut: {
    en: "Capture a frame or edit the frame at the playhead",
    ko: "프레임 캡처 또는 현재 프레임 Annotation 편집",
  },
  removePlayheadFrameShortcut: {
    en: "Remove the captured frame at the playhead",
    ko: "현재 위치의 캡처 프레임 제거",
  },
  saveCameraShortcut: {
    en: "Save annotation changes for the current camera",
    ko: "현재 카메라의 Annotation 변경 사항 저장",
  },
  selectSlotShortcut: {
    en: "Select the corresponding slot",
    ko: "해당 슬롯 선택",
  },
  toggleSlotDisplayShortcut: {
    en: "Toggle selected-only / show-all",
    ko: "선택 항목만 보기 / 전체 항목 보기 전환",
  },
  leftClick: { en: "Left click", ko: "좌클릭" },
  rightClick: { en: "Right click", ko: "우클릭" },
  controlDrag: { en: "Ctrl + drag", ko: "Ctrl + 드래그" },
  controlWheel: { en: "Ctrl + wheel", ko: "Ctrl + 휠" },
  markerClick: { en: "Marker click", ko: "마커 클릭" },
  addSelectedPoint: { en: "Add the selected point type", ko: "선택한 마커 추가" },
  addPositivePoint: { en: "Add a positive point", ko: "포지티브 포인트 추가" },
  addNegativePoint: { en: "Add a negative point", ko: "네거티브 포인트 추가" },
  removeExistingPoint: { en: "Remove the existing point", ko: "기존 포인트 제거" },
  removePoint: { en: "Remove point", ko: "포인트 제거" },
  panFrame: { en: "Pan the captured frame", ko: "캡처된 프레임 이동" },
  zoomAtPointer: { en: "Zoom at the pointer", ko: "포인터 위치에서 확대·축소" },
  zoomIn: { en: "Zoom in", ko: "확대" },
  zoomOut: { en: "Zoom out", ko: "축소" },
  resetView: { en: "Reset view", ko: "화면 초기화" },
  timelineZoom: { en: "Timeline zoom", ko: "타임라인 배율" },
  timelineZoomIn: { en: "Zoom timeline in", ko: "타임라인 확대" },
  timelineZoomOut: { en: "Zoom timeline out", ko: "타임라인 축소" },
  resetTimelineZoom: { en: "Reset timeline zoom", ko: "타임라인 배율 초기화" },
  playbackRate: { en: "Playback speed", ko: "재생 속도" },
  previousTimelineSegment: { en: "Show previous timeline segment", ko: "이전 타임라인 구간 보기" },
  nextTimelineSegment: { en: "Show next timeline segment", ko: "다음 타임라인 구간 보기" },
  frame: { en: "Frame", ko: "프레임" },
  captureMetadata: { en: "Capture metadata", ko: "캡처 메타데이터" },
  annotationStatus: { en: "Annotation", ko: "Annotation" },
  annotationActions: { en: "Annotation actions", ko: "Annotation 작업" },
  workflowStatus: { en: "Workflow status", ko: "작업 상태" },
  segmentationStatus: { en: "Segmentation", ko: "Segmentation" },
  trackingStatus: { en: "Tracking preview", ko: "트래킹 미리보기" },
  saveStatus: { en: "Save status", ko: "저장 상태" },
  ready: { en: "Ready", ko: "준비됨" },
  notStarted: { en: "Not started", ko: "시작 전" },
  track: { en: "Preview tracking", ko: "트래킹 미리보기" },
  trackingStarting: { en: "Starting tracking preview", ko: "트래킹 미리보기 시작 중" },
  trackingRunning: { en: "Tracking preview running", ko: "트래킹 미리보기 실행 중" },
  trackingComplete: { en: "Tracking preview complete", ko: "트래킹 미리보기 완료" },
  trackingStopped: { en: "Tracking preview paused", ko: "트래킹 미리보기 일시정지됨" },
  stopTracking: { en: "Stop tracking preview", ko: "트래킹 미리보기 중지" },
  pauseTracking: { en: "Pause tracking preview", ko: "트래킹 미리보기 일시정지" },
  resumeTracking: { en: "Resume tracking preview", ko: "트래킹 미리보기 재개" },
  trackingUnavailable: { en: "Tracking preview unavailable", ko: "트래킹 미리보기 사용 불가" },
  // rail의 availability 마크와 실행 버튼(`track`)이 같은 메타포로 읽히도록 어순을 맞춘다.
  trackingAvailable: { en: "Preview tracking available", ko: "트래킹 미리보기 가능" },
  trackingReadyDescription: {
    en: "Preview saved annotations from this camera in the selected episode.",
    ko: "이 카메라에 저장된 Annotation을 선택한 에피소드에서 미리 확인합니다.",
  },
  trackingNeedsMediaContext: {
    en: "Select an episode and camera first.",
    ko: "먼저 에피소드와 카메라를 선택하세요.",
  },
  trackingNeedsFrame: {
    en: "Select a captured frame first.",
    ko: "먼저 편집할 프레임을 선택하세요.",
  },
  trackingWaitForPendingWork: {
    en: "Wait for segmentation or saving to finish.",
    ko: "마스크 생성 또는 저장이 끝날 때까지 기다려 주세요.",
  },
  trackingSaveChangesFirst: {
    en: "Save annotation changes for this camera first.",
    ko: "이 카메라의 Annotation 변경 사항을 먼저 저장하세요.",
  },
  trackingNeedsSavedAnnotation: {
    en: "Save at least one annotated frame for this camera.",
    ko: "이 카메라에서 Annotation된 프레임을 하나 이상 저장하세요.",
  },
  trackingNeedsConflictCheck: {
    en: "Complete the annotation conflict check before tracking.",
    ko: "트래킹 전에 Annotation 충돌 검사를 완료하세요.",
  },
  trackingFailed: { en: "Tracking preview failed", ko: "트래킹 미리보기 실패" },
  trackingPreviewViewport: {
    en: "Tracking preview result",
    ko: "트래킹 미리보기 결과",
  },
  mediaResultViewport: { en: "Media result", ko: "미디어 결과" },
  mediaResultLoading: { en: "Loading result overlay", ko: "결과 오버레이 불러오는 중" },
  mediaResultLayerLoadFailed: {
    en: "The result overlay could not be loaded.",
    ko: "결과 오버레이를 불러오지 못했습니다.",
  },
  trackingFrames: { en: "frames processed", ko: "개 프레임 처리" },
  trackingRunAgain: { en: "Run again", ko: "다시 실행" },
  trackingRetry: { en: "Retry", ko: "다시 시도" },
  retryTracking: { en: "Retry tracking preview", ko: "트래킹 미리보기 다시 시도" },
  closeTracking: { en: "Close tracking preview", ko: "트래킹 미리보기 닫기" },
  trackingPlayResult: { en: "Play tracking result", ko: "트래킹 결과 재생" },
  trackingPauseResult: { en: "Pause tracking result", ko: "트래킹 결과 일시정지" },
  trackingResetResult: { en: "Reset tracking result", ko: "트래킹 결과 처음으로" },
  trackingStepBackward: { en: "Previous tracking frame", ko: "이전 트래킹 프레임" },
  trackingStepForward: { en: "Next tracking frame", ko: "다음 트래킹 프레임" },
  trackingToStart: { en: "Go to tracking start", ko: "트래킹 시작으로 이동" },
  trackingToEnd: { en: "Go to tracking end", ko: "트래킹 끝으로 이동" },
  trackingTimeline: { en: "Tracking result timeline", ko: "트래킹 결과 타임라인" },
  capturedAt: { en: "Captured at", ko: "캡처 시각" },
  resolution: { en: "Resolution", ko: "해상도" },
  revision: { en: "Revision", ko: "리비전" },
  playback: { en: "Playback timeline", ko: "재생 타임라인" },
  currentVideoContext: { en: "Current video context", ko: "현재 영상 컨텍스트" },
  emptyWorkspace: { en: "No sequences are available.", ko: "사용 가능한 에피소드가 없습니다." },
  sequenceDirectoryFilters: { en: "Episode filters", ko: "에피소드 필터" },
  sequenceContentFilter: { en: "Content", ko: "내용" },
  allSequenceContent: { en: "All", ko: "전체" },
  capturedSequenceContent: { en: "Has captured frames", ko: "캡처 프레임 있음" },
  capturedSequenceContentDescription: {
    en: "One or more saved captured frames · includes episodes with saved annotations",
    ko: "저장된 캡처 프레임 1개 이상 · 저장된 Annotation이 있는 에피소드도 포함",
  },
  annotatedSequenceContent: { en: "Has annotations", ko: "Annotation 있음" },
  annotatedSequenceContentDescription: {
    en: "One or more saved annotations",
    ko: "저장된 Annotation 1개 이상",
  },
  sequenceStatusFilter: { en: "Status", ko: "상태" },
  allSequenceStatuses: { en: "All", ko: "전체" },
  needsAttentionSequenceStatus: { en: "Needs attention", ko: "검토 필요" },
  needsAttentionSequenceStatusDescription: {
    en: "Annotation conflicts or unsaved annotation changes",
    ko: "Annotation 충돌 또는 저장하지 않은 Annotation 변경 사항",
  },
  clearSequenceDirectoryFilters: { en: "Clear episode filters", ko: "에피소드 필터 지우기" },
  loadingSequences: { en: "Loading episodes", ko: "에피소드 불러오는 중" },
  loadingMoreSequences: { en: "Loading more episodes", ko: "에피소드 더 불러오는 중" },
  loadingCapturedFrame: { en: "Loading captured frame", ko: "캡처 프레임 불러오는 중" },
  capturedFrameLoadFailed: {
    en: "Couldn't load the captured frame. Select it to retry.",
    ko: "캡처 프레임을 불러오지 못했습니다. 다시 선택해 재시도해 주세요.",
  },
  retryCapturedFrameLoad: {
    en: "Loading failed. Select to retry",
    ko: "불러오기 실패. 선택하여 재시도",
  },
  noSequenceResults: {
    en: "No episodes match the current filter.",
    ko: "현재 필터에 맞는 에피소드가 없습니다.",
  },
  retrySequenceDirectory: { en: "Retry loading episodes", ko: "에피소드 다시 불러오기" },
  countUnavailable: { en: "Count unavailable", ko: "개수 알 수 없음" },
  sequenceResults: { en: "episode results", ko: "개 에피소드 결과" },
  loadedSequenceResults: { en: "episodes loaded", ko: "개 에피소드 불러옴" },
  noCameras: { en: "No cameras are available.", ko: "사용 가능한 카메라가 없습니다." },
  operationFailed: { en: "The operation failed.", ko: "작업에 실패했습니다." },
  reviewOnly: { en: "Read-only", ko: "읽기 전용" },
  reviewEditingUnavailable: { en: "Editing unavailable", ko: "편집할 수 없음" },
  reviewOnlyDescription: {
    en: "You can still select frames and targets and change target visibility.",
    ko: "프레임과 타겟을 선택하고 타겟 표시 여부를 바꿔 내용을 확인할 수 있습니다.",
  },
  cameraSelect: { en: "Camera selection", ko: "카메라 선택" },
  searchStreams: { en: "Search cameras", ko: "카메라 검색" },
  clearSearch: { en: "Clear search", ko: "검색어 지우기" },
  noResults: { en: "No matching cameras.", ko: "일치하는 카메라가 없습니다." },
} satisfies Record<string, AnnotationStudioLocalizedText>;

export type AnnotationStudioMessageKey = keyof typeof annotationStudioMessages;

export function annotationStudioText(
  key: AnnotationStudioMessageKey,
  locale: AnnotationStudioLocale,
): string {
  return annotationStudioMessages[key][locale];
}

export function annotationStudioNewSequenceSummary(
  count: number,
  locale: AnnotationStudioLocale,
): string {
  return locale === "ko" ? `신규 ${count}개` : `${count} new`;
}

export function annotationStudioSequenceDirectoryCount(
  resultCount: number | undefined,
  loadedCount: number,
  totalSequenceCount: number,
  locale: AnnotationStudioLocale,
): string {
  if (locale === "ko") {
    return resultCount === undefined
      ? `${loadedCount}개 불러옴 · 전체 ${totalSequenceCount}개`
      : `${resultCount}개 결과 · 전체 ${totalSequenceCount}개`;
  }
  return resultCount === undefined
    ? `${loadedCount} loaded · ${totalSequenceCount} total`
    : `${resultCount} results · ${totalSequenceCount} total`;
}

export function resolveAnnotationStudioMessage(
  message: AnnotationStudioMessage | null | undefined,
  locale: AnnotationStudioLocale,
): string | undefined {
  if (message === null || message === undefined) return undefined;
  return typeof message === "string" ? message : message[locale];
}

export function resolveAnnotationOperationAvailabilityReason(
  availability: AnnotationOperationAvailability | null | undefined,
  locale: AnnotationStudioLocale,
): string | undefined {
  return resolveAnnotationStudioMessage(
    availability?.localizedReason ?? availability?.reason,
    locale,
  );
}

export function resolveAnnotationOperationErrorMessage(
  error: AnnotationOperationError | null | undefined,
  locale: AnnotationStudioLocale,
): string | undefined {
  return resolveAnnotationStudioMessage(error?.localizedUserMessage ?? error?.userMessage, locale);
}

export function capturedFrameFilterSummary(
  locale: AnnotationStudioLocale,
  sequenceLabel: string,
  shownCount: number,
  totalCount: number,
): string {
  const hiddenCount = Math.max(0, totalCount - shownCount);
  return locale === "ko"
    ? `${sequenceLabel} · ${totalCount}개 중 ${shownCount}개 표시 · ${hiddenCount}개 숨김`
    : `${sequenceLabel} · ${shownCount} of ${totalCount} shown · ${hiddenCount} hidden`;
}

export function capturedFrameFilterEmptyMessage(
  locale: AnnotationStudioLocale,
  sequenceLabel: string,
  hiddenCount: number,
): string {
  return locale === "ko"
    ? `${sequenceLabel}에는 캡처 프레임이 없습니다. 다른 에피소드의 프레임 ${hiddenCount}개가 필터로 숨겨져 있습니다.`
    : `There are no captured frames in ${sequenceLabel}. ${hiddenCount} ${
        hiddenCount === 1 ? "frame" : "frames"
      } from other episodes ${hiddenCount === 1 ? "is" : "are"} hidden by this filter.`;
}

export function capturedFrameAnnotationSummary(
  locale: AnnotationStudioLocale,
  annotatedTargetCount: number,
): string {
  if (annotatedTargetCount === 0) {
    return locale === "ko" ? "Annotation 없음" : "No annotations";
  }

  if (locale === "ko") {
    return `${annotatedTargetCount}개 Object slot에 Annotation 있음`;
  }

  return annotatedTargetCount === 1
    ? "1 object slot has an annotation"
    : `${annotatedTargetCount} object slots have annotations`;
}

export function captureTimestampAdjustedMessage(
  locale: AnnotationStudioLocale,
  resolvedTimestampLabel: string,
): string {
  return locale === "ko"
    ? `요청한 시각을 사용할 수 없어 ${resolvedTimestampLabel}에 프레임을 캡처했습니다.`
    : `The requested time was unavailable, so the frame was captured at ${resolvedTimestampLabel}.`;
}

export function annotationConflictToastMessage(
  locale: AnnotationStudioLocale,
  issueCount: number,
  frameCount: number,
): string {
  if (locale === "ko") {
    return frameCount > 0
      ? `${frameCount}개 프레임에서 Annotation 충돌 ${issueCount}건이 발견되었습니다.`
      : `Annotation 충돌 ${issueCount}건이 발견되었습니다.`;
  }

  const issueLabel = issueCount === 1 ? "conflict" : "conflicts";
  if (frameCount === 0) return `${issueCount} annotation ${issueLabel} found.`;
  const frameLabel = frameCount === 1 ? "frame" : "frames";
  return `${issueCount} annotation ${issueLabel} found across ${frameCount} ${frameLabel}.`;
}

export function saveConflictToastMessage(
  locale: AnnotationStudioLocale,
  frameCount: number,
): string {
  if (locale === "ko") return `${frameCount}개 프레임에서 저장 충돌이 발생했습니다.`;
  return frameCount === 1
    ? "A save conflict was found in 1 frame."
    : `Save conflicts were found in ${frameCount} frames.`;
}

export function trackingPreviewFailureToastMessage(
  locale: AnnotationStudioLocale,
  mediaContext: string,
  reason?: string,
): string {
  return [annotationStudioText("trackingFailed", locale), mediaContext, reason]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
}

export function mergeAnnotationStudioLabels(
  labels?: Partial<AnnotationStudioLabels>,
): AnnotationStudioLabels {
  return { ...defaultAnnotationStudioLabels, ...labels };
}
